import { useLocation } from "@remix-run/react";

import {
  DEFAULT_LANGUAGE,
  SITE_URL,
  SUPPORTED_LANGUAGES,
} from "~/config/constants";
import { LanguageContextType, useLanguage } from "~/context/LanguageContext";
import { utmSearch } from "~/helpers/attribution";

type Lang = LanguageContextType["lang"];

/**
 * English lives at the root (`/courses`); every other locale is prefixed
 * (`/fr/courses`). Pure, so it can be used in loaders, meta and the sitemap.
 */
export function localizePath(lang: Lang, path: string): string {
  const clean = path === "/" ? "" : path;

  if (!SUPPORTED_LANGUAGES.includes(lang) || lang === DEFAULT_LANGUAGE) {
    return clean || "/";
  }

  return `/${lang}${clean}`;
}

/** Absolute URL for a path in a given locale. Used for canonical + hreflang. */
export function localizeUrl(lang: Lang, path: string): string {
  const localized = localizePath(lang, path);
  return `${SITE_URL}${localized === "/" ? "/" : localized}`;
}

/** Strips a leading locale segment, yielding the locale-independent path. */
export function stripLocale(pathname: string): string {
  const [, maybeLocale, ...rest] = pathname.split("/");

  if (SUPPORTED_LANGUAGES.includes(maybeLocale as Lang)) {
    return `/${rest.join("/")}`.replace(/\/$/, "") || "/";
  }

  return pathname.replace(/\/$/, "") || "/";
}

/** Rewrites the current path to another locale, preserving the page. */
export function switchLanguagePath(pathname: string, next: Lang): string {
  return localizePath(next, stripLocale(pathname));
}

/**
 * Hook form, for components that just need "link to this page in my locale".
 *
 * Also re-appends any utm_* params of the current URL, so campaign attribution
 * survives internal navigation without storing anything on the device. The
 * canonical and hreflang URLs use the pure functions above and stay clean.
 */
export function useLocalizedPath(): (path: string) => string {
  const { lang } = useLanguage();
  const { search } = useLocation();
  const utm = utmSearch(search);

  return (path: string) => {
    const localized = localizePath(lang, path);
    if (!utm) return localized;
    return localized.includes("?")
      ? `${localized}&${utm.slice(1)}`
      : `${localized}${utm}`;
  };
}
